import {
  useAccounts,
  useJ1AuthFetch,
  useUser,
} from '@jupiterone/jupiter-web-toolkit/src';
import {
  EInvitationStatus,
  IPaginatedResponse,
} from '@jupiterone/jupiter-web-toolkit/src/types';
import urls from '@jupiterone/jupiter-web-toolkit/src/urls';
import retryIfTimeout from '@jupiterone/jupiter-web-toolkit/src/util/retryIfTimeout';

const useInviteClient = () => {
  const { authFetch } = useJ1AuthFetch();
  const { user, loading } = useUser();
  const { account } = useAccounts();

  const getInvitations = async (): Promise<IPaginatedResponse> => {
    const defaultPageSize = 5000;
    if (!user) {
      throw new Error('user is required for revoking an invite');
    }
    const encodedUser = encodeURIComponent(user?.id);
    return retryIfTimeout(() => {
      return authFetch(
        `${urls.invitations}?pageSize=${defaultPageSize}&user=${encodedUser}`,
        {
          method: 'GET',
        }
      );
    });
  };

  const inviteAction = async (
    id: string,
    action: EInvitationStatus
  ): Promise<void> => {
    await retryIfTimeout(() => {
      return authFetch(`${urls.invitations}/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: action }),
      });
    });
  };

  const sendInvite = (email: string, groupId: string) => {
    return authFetch(`${urls.invitations}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email: email.trim(), group: groupId.trim() }),
    });
  };

  const sendPlayAccountInvite = (email: string) => {
    return authFetch(`${urls.invitations}?type=play`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email: email.trim() }),
    });
  };

  const getAccountInvitations = (): Promise<IPaginatedResponse> => {
    const defaultPageSize = 5000;

    if (!account) {
      throw new Error('Need to be logged into an account');
    }

    const accountURI = encodeURIComponent(account.id);
    return retryIfTimeout(() => {
      return authFetch(
        `${urls.invitations}?pageSize=${defaultPageSize}&account=${accountURI}`,
        {
          method: 'GET',
        }
      );
    });
  };

  return {
    loading,
    getInvitations,
    inviteAction,
    sendInvite,
    getAccountInvitations,
    sendPlayAccountInvite,
  };
};

export default useInviteClient;
